import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";

export const CLASS_COLS = ["G1", "G2", "G3", "Listed", "Open", "C6", "C5", "C4", "C3", "C2", "C1", "Maiden"];
export const BM_COLS = ["120+", "110-119", "100-109", "90-99", "85-89", "80-84", "75-79", "70-74", "65-69", "60-64", "55-59", "50-54", "45-49", "≤44"];
const DAY_RULES = [
  ["Metropolitan", "Saturday → Saturday Metropolitan • every other day & public holidays → Midweek Metropolitan"],
  ["Provincial", "Tue / Wed / Thu / Sat & public holidays → Midweek Provincial • Mon / Fri / Sun → Midweek Country"],
  ["Country", "Always → Midweek Country"],
  ["Age races", "2YO-only / 3YO-only races (e.g. 3YO Handicap) use only the 2 YO / 3 YO table at the same level • 3YO & Up (and other '& Upwards') races use the open-age tables above"],
];
const LEVELS = ["Metropolitan", "Provincial", "Country"];
const GROUPS = ["Saturday", "Midweek", "3 YO", "2 YO"];
const rank = (name) => {
  const l = LEVELS.findIndex((x) => name.endsWith(x));
  const g = GROUPS.findIndex((x) => name.startsWith(x));
  return (l < 0 ? 9 : l) * 10 + (g < 0 ? 9 : g);
};
const cell = "w-12 bg-[#0F172A] border border-slate-800 rounded px-1 py-1 text-xs num text-center focus:outline-none focus:border-emerald-500 transition-colors";

const TrackList = ({ label, value, onChange, testId }) => {
  const [text, setText] = useState(value.join(", "));
  return (
    <label className="block">
      <span className="eyebrow">{label} ({value.length})</span>
      <Textarea
        data-testid={testId}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => onChange(text.split(",").map((s) => s.trim()).filter(Boolean))}
        className="mt-1.5 bg-[#0F172A] min-h-[90px] text-sm"
      />
    </label>
  );
};

const ScoreGrid = ({ title, cols, kind, tables, onChange }) => (
  <div className="panel p-5">
    <div className="eyebrow mb-3">{title}</div>
    <div className="scroll-x">
      <table className="text-xs" data-testid={`class-grid-${kind}`}>
        <thead>
          <tr className="text-slate-500 num">
            <th className="text-left font-normal pr-3 py-1">Table</th>
            {cols.map((c) => <th key={c} className="font-normal px-0.5 py-1 whitespace-nowrap">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {tables.map((t, ti) => ({ t, ti })).sort((a, b) => rank(a.t.name) - rank(b.t.name)).map(({ t, ti }, row) => (
            <tr key={t.name} className={row && row % 4 === 0 ? "border-t border-slate-700" : ""} data-testid={`class-row-${kind}-${row}`}>
              <td className="pr-3 py-0.5 whitespace-nowrap text-slate-300">{t.name}</td>
              {cols.map((c, ci) => (
                <td key={c} className="px-0.5 py-0.5">
                  <input
                    data-testid={`class-cell-${kind}-${ti}-${ci}`}
                    type="number"
                    value={t[kind][ci] ?? ""}
                    onChange={(e) => onChange(ti, kind, ci, e.target.value === "" ? null : Number(e.target.value))}
                    className={cell}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const LetterScores = ({ value, onChange }) => (
  <div className="panel p-5 flex flex-wrap items-center gap-4" data-testid="letter-scores-panel">
    <span className="eyebrow">Class A–E scores</span>
    {["A", "B", "C", "D", "E"].map((k) => (
      <label key={k} className="flex items-center gap-2 text-sm text-slate-300">Class {k}
        <input type="number" min={0} max={200} value={value?.[k] ?? ""} data-testid={`letter-score-${k}-input`}
          onChange={(e) => onChange({ ...value, [k]: e.target.value === "" ? null : Number(e.target.value) })} className={`${cell} w-16`} />
      </label>
    ))}
    <p className="text-xs text-slate-500 max-w-xl basis-full">Used at any track and day. The weight change after a Class A–E run is ignored (0 points).</p>
  </div>
);

export const ClassTablesEditor = ({ tracks, tables, onTracks, onTables, letters, onLetters }) => {
  const setCell = (ti, kind, ci, v) =>
    onTables(tables.map((t, i) => (i === ti ? { ...t, [kind]: t[kind].map((x, j) => (j === ci ? v : x)) } : t)));
  return (
    <div className="space-y-6" data-testid="class-tables-editor">
      <div className="panel p-5 grid md:grid-cols-2 gap-5">
        <TrackList label="Metropolitan Tracks" value={tracks.metropolitan} onChange={(v) => onTracks({ ...tracks, metropolitan: v })} testId="tracks-metro-input" />
        <TrackList label="Provincial Tracks" value={tracks.provincial} onChange={(v) => onTracks({ ...tracks, provincial: v })} testId="tracks-provincial-input" />
        <p className="text-xs text-slate-500 md:col-span-2">Any track not listed is a Country track. Names match if the venue contains them (e.g. "Sportsbet Pakenham" → Pakenham).</p>
      </div>
      <div className="panel p-5">
        <div className="eyebrow mb-3">Day Rules (AEST date of the meeting, public holidays by state)</div>
        <ul className="space-y-1.5 text-sm">
          {DAY_RULES.map(([k, v]) => (
            <li key={k}><span className="text-emerald-400 font-medium">{k}:</span> <span className="text-slate-300">{v}</span></li>
          ))}
        </ul>
      </div>
      <ScoreGrid title="Class Scores (Group / Listed / Open / Class / Maiden)" cols={CLASS_COLS} kind="class" tables={tables} onChange={setCell} />
      <ScoreGrid title="Benchmark Scores" cols={BM_COLS} kind="bm" tables={tables} onChange={setCell} />
      <LetterScores value={letters} onChange={onLetters} />
    </div>
  );
};
